import React                                                   from 'react'
import {Box, Drawer, IconButton, Tooltip, Typography, useTheme} from '@mui/material'
import {Close}                                                 from '@mui/icons-material'
import AchievementBadgeIcon                                    from './AchievementBadgeIcon'
import {groupByCategory}                                       from '../../util/achievements'

/*
 * The user's badge collection for one prepkit.
 *
 * Right-hand drawer matching TopicDetailsDrawer in PrepKitDashboard.js. Shows every award,
 * not just earned ones, so unearned badges read as goals -- that is the point of a
 * collection. Earned badges sort first inside each category.
 *
 * Presentational: the caller owns the fetch and passes `data` straight from
 * GET /prepkit/:id/achievements. That keeps one copy of the badge data on the screen, since
 * the dashboard tile needs the same numbers this panel does.
 */

const fmtEarnedAt = (iso) => {
	if (!iso) return null
	try {
		return new Date(iso).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'})
	}
	catch (e) {
		return null
	}
}

const BadgeTile = ({achievement, custom}) => {
	const {name, description, category, token, earned, earnedAt, detectable} = achievement
	const earnedOn = fmtEarnedAt(earnedAt)

	// an award with no trigger in the product yet is a promise, not a failure
	const subtitle = earned
		? (earnedOn ? `Earned ${earnedOn}` : 'Earned')
		: detectable ? description : 'Coming soon'

	return (
		<Tooltip title={description} arrow enterDelay={400}>
			<Box
				sx={{
					display: 'flex', gap: 1.5, alignItems: 'center',
					p: 1.25,
					borderRadius: `${custom.radius.md}px`,
					background: earned ? custom.bg.raised : 'transparent',
					border: `1px solid ${earned ? custom.border.strong : custom.border.color}`,
					opacity: detectable ? 1 : 0.7,
				}}
			>
				<AchievementBadgeIcon token={token} category={category} size={48} earned={earned} title={name}/>
				<Box sx={{minWidth: 0}}>
					<Typography sx={{
						fontSize: custom.fontSize.base,
						fontWeight: earned ? custom.fontWeight.semibold : custom.fontWeight.medium,
						color: earned ? custom.text.main : custom.text.muted,
					}}>
						{name}
					</Typography>
					<Typography sx={{
						fontSize: custom.fontSize.xs,
						color: earned ? custom.status.green : custom.text.subtopic,
					}}>
						{subtitle}
					</Typography>
				</Box>
			</Box>
		</Tooltip>
	)
}

const AchievementsPanel = ({data, open, onClose}) => {
	const theme = useTheme()
	const custom = theme.custom
	const groups = groupByCategory(data)

	return (
		<Drawer
			anchor="right"
			open={Boolean(open)}
			onClose={onClose}
			PaperProps={{sx: {width: {xs: '100%', sm: 460}, background: custom.bg.surface, color: custom.text.main}}}
		>
			<Box sx={{p: 3}}>
				<Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1}}>
					<Box>
						<Typography sx={{fontSize: custom.fontSize.display, fontWeight: custom.fontWeight.bold, color: custom.text.main}}>
							Achievements
						</Typography>
						<Typography sx={{mt: 0.25, fontSize: custom.fontSize.sm, color: custom.text.muted}}>
							{data
								? `${data.earnedCount} of ${data.earnableCount} badges earned`
								: 'Loading your badges…'}
						</Typography>
					</Box>
					<IconButton size="small" onClick={onClose}><Close fontSize="small"/></IconButton>
				</Box>

				{groups.map(({category, achievements}) => (
					<Box key={category} sx={{mt: 3}}>
						<Typography sx={{
							fontSize: custom.fontSize.xxs,
							fontWeight: custom.fontWeight.semibold,
							textTransform: 'uppercase',
							letterSpacing: .5,
							color: custom.text.subtopic,
							mb: 1,
						}}>
							{category}
						</Typography>
						<Box sx={{display: 'grid', gap: 1}}>
							{achievements.map(a => <BadgeTile key={a.token} achievement={a} custom={custom}/>)}
						</Box>
					</Box>
				))}
			</Box>
		</Drawer>
	)
}

export default AchievementsPanel
